import type { InfraEnvironmentSummary, SandboxRunState } from "@/lib/api";
import { formatElapsed } from "@/lib/format-datetime";
import type { InfraStatusTone } from "@/lib/infra-placement";

export type InfraEnvironmentKind = "cloud" | "local";

export const INFRA_ENVIRONMENT_KINDS: readonly InfraEnvironmentKind[] = ["cloud", "local"];

export const INFRA_ENVIRONMENT_KIND_LABELS: Record<InfraEnvironmentKind, string> = {
	cloud: "クラウド",
	local: "ローカル",
};

export function infraEnvironmentKind(environment: InfraEnvironmentSummary): InfraEnvironmentKind {
	return environment.runner === null ? "cloud" : "local";
}

export interface InfraEnvironmentOrganization {
	id: string;
	name: string;
	environmentCount: number;
}

/** 環境を持つ組織を名前順に並べる。 */
export function infraEnvironmentOrganizations(
	environments: readonly InfraEnvironmentSummary[],
): InfraEnvironmentOrganization[] {
	const byId = new Map<string, InfraEnvironmentOrganization>();
	for (const environment of environments) {
		const organization = byId.get(environment.organizationId);
		if (organization) organization.environmentCount++;
		else
			byId.set(environment.organizationId, {
				id: environment.organizationId,
				name: environment.organizationName,
				environmentCount: 1,
			});
	}
	return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "ja"));
}

/** 種類 (何も選んでいなければすべて) と組織 (null ならすべて) で絞り、稼働の長い順、続けて停止中を並べる。 */
export function filterInfraEnvironments(
	environments: readonly InfraEnvironmentSummary[],
	selectedKinds: ReadonlySet<InfraEnvironmentKind>,
	organizationId: string | null,
): InfraEnvironmentSummary[] {
	return environments
		.filter(
			(e) =>
				(selectedKinds.size === 0 || selectedKinds.has(infraEnvironmentKind(e))) &&
				(organizationId === null || e.organizationId === organizationId),
		)
		.map((e, index) => ({ e, index }))
		.sort((a, b) => startedAtOrder(a.e) - startedAtOrder(b.e) || a.index - b.index)
		.map(({ e }) => e);
}

function startedAtOrder(environment: InfraEnvironmentSummary): number {
	return environment.startedAt === null
		? Number.POSITIVE_INFINITY
		: new Date(environment.startedAt).getTime();
}

const RUN_STATE_STATUSES: Record<SandboxRunState, { label: string; tone: InfraStatusTone }> = {
	running: { label: "稼働中", tone: "success" },
	pending: { label: "起動中", tone: "warning" },
	waitingForCapacity: { label: "容量待ち", tone: "warning" },
	shuttingDown: { label: "終了中", tone: "warning" },
	errored: { label: "異常", tone: "danger" },
};

/** tone が無い状態 (停止中) は色を付けない。オフラインのデバイスの環境は状態が分からないため、オフラインとして出す。 */
export function infraEnvironmentSummaryStatus(environment: InfraEnvironmentSummary): {
	label: string;
	tone?: InfraStatusTone;
} {
	if (environment.runner !== null && !environment.runner.online)
		return { label: "オフライン", tone: "danger" };
	if (environment.runState !== null) return RUN_STATE_STATUSES[environment.runState];
	if (environment.startupFailed) return { label: "起動失敗", tone: "danger" };
	return { label: "停止" };
}

/** 稼働時間を「2日3時間」「1時間5分」の形で返す。 */
export function formatUptime(ms: number): string {
	const hours = Math.floor(Math.max(0, ms) / 3_600_000);
	if (hours < 24) return formatElapsed(ms);
	return `${Math.floor(hours / 24)}日${hours % 24}時間`;
}
